/**
 * HelpHub — Cabinet › Mode d'emploi et aide (Batch #14.302). Liste des guides + recherche plein texte.
 * Route : /admin/aide
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LifeBuoy, Search, FileText } from "lucide-react";
import { api } from "@/api";
import { ICONS, TONES, searchGuides } from "./aide/_help";

export default function HelpHub() {
  const navigate = useNavigate();
  const [guides, setGuides] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/help/guides", { params: { full: 1 } }).then((r) => setGuides(r.data.items || [])).catch(() => setGuides([])); }, []);
  const hits = useMemo(() => (guides ? searchGuides(guides, q) : []), [guides, q]);
  const groups = useMemo(() => {
    const m = new Map();
    (guides || []).forEach((g) => { if (!m.has(g.category)) m.set(g.category, []); m.get(g.category).push(g); });
    return [...m.entries()];
  }, [guides]);

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="help-hub">
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 rounded-2xl p-5 sm:p-7 shadow-sm text-white">
        <button onClick={() => navigate("/admin/cabinet")} className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white mb-3" data-testid="help-back-cabinet"><ArrowLeft className="w-3.5 h-3.5" /> Cabinet</button>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center"><LifeBuoy className="w-6 h-6" /></div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold">Mode d'emploi et aide</h1>
            <p className="text-xs text-slate-300 mt-0.5">Guides pas à pas et feuillets explicatifs du cabinet — lisibles à l'écran ou en PDF.</p>
          </div>
        </div>
        <div className="relative mt-5 max-w-xl">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un mot-clé (ex. prélèvement, apprenti, rapport mensuel…)"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="help-search-input" />
        </div>
      </div>

      {q.trim().length >= 2 && (
        <div className="space-y-2" data-testid="help-search-results">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{hits.length} résultat{hits.length > 1 ? "s" : ""} pour « {q} »</div>
          {hits.length === 0 && <p className="text-sm text-slate-500">Aucune section ne correspond. Essayez un autre mot.</p>}
          {hits.map((h, i) => (
            <Link key={i} to={`/admin/aide/${h.guide.id}#${h.section.id}`} className="block rounded-xl border border-slate-200 bg-white p-3.5 hover:border-slate-400 hover:shadow-sm transition" data-testid={`help-search-hit-${i}`}>
              <div className="text-[11px] text-slate-500">{h.guide.title}</div>
              <div className="text-sm font-semibold text-slate-900">{h.section.title}</div>
              <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">…{h.snippet}…</div>
            </Link>
          ))}
        </div>
      )}

      {guides === null && <p className="text-sm text-slate-500">Chargement…</p>}
      {groups.map(([cat, items]) => (
        <section key={cat} className="space-y-3">
          <h2 className="text-base md:text-lg font-bold text-slate-900">{cat}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((g) => {
              const Icon = ICONS[g.icon] || FileText;
              const tone = TONES[g.tone] || TONES.slate;
              return (
                <button key={g.id} onClick={() => navigate(`/admin/aide/${g.id}`)}
                  className={`group text-left bg-gradient-to-br ${tone.tile} border-2 rounded-2xl p-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5`} data-testid={`help-guide-tile-${g.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0 group-hover:shadow-md transition"><Icon className="w-6 h-6" /></div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">{g.title}</div>
                      <div className="text-xs opacity-80 mt-1 leading-snug">{g.subtitle}</div>
                      <div className="text-[10px] opacity-60 mt-2">{g.sections.length} sections · mis à jour le {g.updated}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
